import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { useAppLoading } from '@/stores/app/loading';
import { getQuarter } from '@/helpers/date';
import { fetchInboundReport } from '@/api/insights';
import { format } from 'date-fns';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { getPassthroughRate, getRowsForCategory } from '@/stores/insights/helpers';
import { assignInsightsTableToCSVDownloadLink } from '@/helpers/fileDownload';
import { inboundOutboundCSVHeaders } from '@/helpers/constants/insightsCSVHeaders';
import { numberWithCommas } from '@/helpers/formatters';
import { useDatasetCounts } from './datasetCounts';

export const useInboundReport = defineStore('inboundReport', () => {
  const paidVsFreeData = ref([]);
  const generalVsNicheData = ref([]);
  const error = ref(null);
  const isGeneralVsNiche = ref(false);

  const data = computed(() => (isGeneralVsNiche.value
    ? generalVsNicheData.value
    : paidVsFreeData.value));

  const isEmptyData = computed(() => {
    if (!Array.isArray(data.value) || data.value.length === 0) {
      return true;
    }

    return data.value.every((category) => {
      // If sources array is missing or empty, treat as empty
      if (!Array.isArray(category.sources) || category.sources.length === 0) {
        return true;
      }

      // Check if all sources in this category have all zero values for the relevant fields
      return category.sources.every((source) => ['review', 'screen', 'assessment', 'offer', 'hired'].every(
        (key) => (source[key] || 0) === 0,
      ));
    });
  });

  const flatListOfSources = computed(() => data.value.map(({ sources }) => sources).flat());

  const categoryTotals = computed(() => data.value.map((category) => {
    const total = category.sources.reduce((acc, source) => {
      acc.review += source.review || 0;
      acc.screen += source.screen || 0;
      acc.assessment += source.assessment || 0;
      acc.offer += source.offer || 0;
      acc.hired += source.hired || 0;
      return acc;
    }, {
      review: 0,
      screen: 0,
      assessment: 0,
      offer: 0,
      hired: 0,
    });

    return {
      category: category.source_category,
      ...total,
    };
  }).sort((a, b) => b.review - a.review));

  const otherInboundRows = computed(() => {
    const otherData = data.value.find((category) => category.source_category === 'other') || {};
    return getRowsForCategory(otherData, 'Other Inbound');
  });

  const firstGroupRows = computed(() => {
    const firstGroupKey = isGeneralVsNiche.value ? 'general' : 'free';
    const firstGroupLabel = isGeneralVsNiche.value ? 'General Job Boards' : 'Free Job Boards';
    const prospectingData = data.value
      .find((category) => category.source_category === firstGroupKey) || {};

    return getRowsForCategory(prospectingData, firstGroupLabel);
  });

  const secondGroupRows = computed(() => {
    const secondGroupKey = isGeneralVsNiche.value ? 'niche' : 'paid';
    const secondGroupLabel = isGeneralVsNiche.value ? 'Niche Job Boards' : 'Paid Job Boards';
    const prospectingData = data.value
      .find((category) => category.source_category === secondGroupKey) || {};

    return getRowsForCategory(prospectingData, secondGroupLabel);
  });

  const tableTotals = computed(() => {
    const totalApplied = categoryTotals.value.reduce((acc, item) => acc + item.review, 0);
    const totalScreening = categoryTotals.value.reduce((acc, item) => acc + item.screen, 0);
    const totalAssessment = categoryTotals.value.reduce((acc, item) => acc + item.assessment, 0);
    const totalOffer = categoryTotals.value.reduce((acc, item) => acc + item.offer, 0);
    const totalHired = categoryTotals.value.reduce((acc, item) => acc + item.hired, 0);

    const appliedToScreeningPassthroughRate = getPassthroughRate('applied', 'screening', {
      applied: totalApplied,
      screening: totalScreening,
    });
    const screeningToAssessmentPassthroughRate = getPassthroughRate('screening', 'assessment', {
      screening: totalScreening,
      assessment: totalAssessment,
    });
    const assessmentToOfferPassthroughRate = getPassthroughRate('assessment', 'offer', {
      assessment: totalAssessment,
      offer: totalOffer,
    });
    const offerToHiredPassthroughRate = getPassthroughRate('offer', 'hired', {
      offer: totalOffer,
      hired: totalHired,
    });

    return [
      {
        id: 'applied',
        value: totalApplied,
        percent: appliedToScreeningPassthroughRate,
        isPassthrough: true,
      },
      {
        id: 'screening',
        value: totalScreening,
        percent: screeningToAssessmentPassthroughRate,
        isPassthrough: true,
      },
      {
        id: 'assessment',
        value: totalAssessment,
        percent: assessmentToOfferPassthroughRate,
        isPassthrough: true,
      },
      {
        id: 'offer',
        value: totalOffer,
        percent: offerToHiredPassthroughRate,
        isPassthrough: true,
      },
      {
        id: 'hired',
        value: totalHired,
      },
    ];
  });

  const calculateCandidatesForSummary = () => numberWithCommas(categoryTotals.value
    .reduce((acc, item) => acc + item.review, 0));

  const clearData = () => {
    paidVsFreeData.value = [];
    generalVsNicheData.value = [];
    error.value = null;
    isGeneralVsNiche.value = false;
  };

  const updateIsGeneralVsNiche = (value) => {
    isGeneralVsNiche.value = value;
  };

  const loadInboundReport = async ({
    selectedDateRange,
    routeQuery,
    selectedFilters,
    selectedMyJobs,
    isToggle = false,
  }) => {
    error.value = null;

    if (!isToggle) {
      clearData();
    }

    const lastQuarter = getQuarter(1);
    const fallbackDateRange = selectedDateRange || { from: format(lastQuarter[0], 'yyyy-MM-dd'), to: format(lastQuarter[1], 'yyyy-MM-dd') };
    const date = routeQuery?.from && routeQuery?.to
      ? { from: routeQuery.from, to: routeQuery.to }
      : fallbackDateRange;

    useAppLoading().setLoading({
      pageLoading: true,
    });

    try {
      const responseData = await fetchInboundReport({
        selectedDateRange: date,
        routeQuery,
        filters: selectedFilters,
        owned: selectedMyJobs,
        isGeneralVsNiche: isGeneralVsNiche.value,
      });

      if (isGeneralVsNiche.value) {
        generalVsNicheData.value = responseData;
      } else {
        paidVsFreeData.value = responseData;
      }

      if (responseData?.length) {
        useDatasetCounts().setDatasetCounts(calculateCandidatesForSummary());
      }
    } catch (err) {
      useAppSnackbar().addErrorSnackbar({
        text: `Unable to load inbound report - ${err.message}`,
        timeout: 3500,
      });
      error.value = err;
      throw err;
    } finally {
      useAppLoading().setLoading({
        pageLoading: false,
      });
    }
  };

  const toggleGeneralVsNiche = async (params) => {
    const isNowGeneralVsNiche = !isGeneralVsNiche.value;
    isGeneralVsNiche.value = isNowGeneralVsNiche;

    if ((isNowGeneralVsNiche && generalVsNicheData.value.length === 0)
        || (!isNowGeneralVsNiche && paidVsFreeData.value.length === 0)) {
      await loadInboundReport({ ...params, isToggle: true });
    }
  };

  const downloadInboundPaidVsFreeTable = async ({
    dateRange, projectName, routeQuery, router,
  }) => {
    let shouldRevertToggle = false;

    if (paidVsFreeData.value.length === 0) {
      await toggleGeneralVsNiche({
        routeQuery: routeQuery || '',
        selectedMyJobs: routeQuery?.owned === 'true',
        router,
      });

      shouldRevertToggle = true;
    } else if (isGeneralVsNiche.value) {
      isGeneralVsNiche.value = false;
      shouldRevertToggle = true;
    }

    const firstData = data.value
      .find((category) => category.source_category === 'free') || {};
    const first = getRowsForCategory(firstData, 'Free Job Boards').map((row) => ({
      label: row.label,
      source_category_label: 'Free Job Boards',
      review: row.review,
      screen: row.screen,
      assessment: row.assessment,
      offer: row.offer,
      hired: row.hired,
    }));

    const secondData = data.value
      .find((category) => category.source_category === 'paid') || {};
    const second = getRowsForCategory(secondData, 'Paid Job Boards').map((row) => ({
      label: row.label,
      source_category_label: 'Paid Job Boards',
      review: row.review,
      screen: row.screen,
      assessment: row.assessment,
      offer: row.offer,
      hired: row.hired,
    }));

    const otherInboundRowsWithCategory = otherInboundRows.value.map((row) => ({
      label: row.label,
      source_category_label: 'Other Inbound',
      review: row.review,
      screen: row.screen,
      assessment: row.assessment,
      offer: row.offer,
      hired: row.hired,
    }));

    assignInsightsTableToCSVDownloadLink({
      tableRows: [...otherInboundRowsWithCategory, ...first, ...second],
      tableHeaders: inboundOutboundCSVHeaders,
      tableTotals: tableTotals.value,
      dateRange,
      projectName,
      page: 'inbound-report-paid-vs-free',
      showTotalAsCategory: true,
    });

    if (shouldRevertToggle) isGeneralVsNiche.value = !isGeneralVsNiche.value;
  };

  const downloadInboundGeneralVsNicheTable = async ({
    dateRange, projectName, routeQuery, router,
  }) => {
    let shouldRevertToggle = false;

    if (generalVsNicheData.value.length === 0) {
      await toggleGeneralVsNiche({
        routeQuery: routeQuery || '',
        selectedMyJobs: routeQuery?.owned === 'true',
        router,
      });

      shouldRevertToggle = true;
    } else if (!isGeneralVsNiche.value) {
      isGeneralVsNiche.value = true;
      shouldRevertToggle = true;
    }

    const firstData = data.value
      .find((category) => category.source_category === 'general') || {};
    const first = getRowsForCategory(firstData, 'General Job Boards').map((row) => ({
      label: row.label,
      source_category_label: 'General Job Boards',
      review: row.review,
      screen: row.screen,
      assessment: row.assessment,
      offer: row.offer,
      hired: row.hired,
    }));

    const secondData = data.value
      .find((category) => category.source_category === 'niche') || {};
    const second = getRowsForCategory(secondData, 'Niche Job Boards').map((row) => ({
      label: row.label,
      source_category_label: 'Niche Job Boards',
      review: row.review,
      screen: row.screen,
      assessment: row.assessment,
      offer: row.offer,
      hired: row.hired,
    }));

    const otherInboundRowsWithCategory = otherInboundRows.value.map((row) => ({
      label: row.label,
      source_category_label: 'Other Inbound',
      review: row.review,
      screen: row.screen,
      assessment: row.assessment,
      offer: row.offer,
      hired: row.hired,
    }));

    assignInsightsTableToCSVDownloadLink({
      tableRows: [...otherInboundRowsWithCategory, ...first, ...second],
      tableHeaders: inboundOutboundCSVHeaders,
      tableTotals: tableTotals.value,
      dateRange,
      projectName,
      page: 'inbound-report-general-vs-niche',
      showTotalAsCategory: true,
    });

    if (shouldRevertToggle) isGeneralVsNiche.value = !isGeneralVsNiche.value;
  };

  return {
    // State
    data,
    error,
    isGeneralVsNiche,
    otherInboundRows,
    firstGroupRows,
    secondGroupRows,
    tableTotals,

    // Computed
    isEmptyData,
    categoryTotals,
    flatListOfSources,

    // Actions
    loadInboundReport,
    toggleGeneralVsNiche,
    updateIsGeneralVsNiche,
    clearData,
    downloadInboundPaidVsFreeTable,
    downloadInboundGeneralVsNicheTable,
  };
});
